import { z } from 'zod'

export const MAX_FIGMA_EXPORT_FRAMES = 20
export const MAX_FIGMA_EXPORT_NODES = 4000
export const MAX_FIGMA_TRANSFER_BYTES = 32 * 1024 * 1024

const number = z.number().finite()
const unit = number.min(0).max(1)
const color = z.object({ r: unit, g: unit, b: unit, a: unit })
const rect = z.object({
  x: number.min(-100000).max(100000),
  y: number.min(-100000).max(100000),
  width: number.positive().max(30000),
  height: number.positive().max(30000),
})
const run = z.object({
  text: z.string().max(100000),
  family: z.string().max(200),
  weight: number.min(1).max(1000),
  italic: z.boolean(),
  size: number.positive().max(1000),
  color,
  spacing: number.min(-1000).max(1000),
  decoration: z.enum(['NONE', 'UNDERLINE', 'STRIKETHROUGH']),
})
const base = z.object({
  name: z.string().max(200),
  // Parents precede their children. Flat storage also bounds parsing depth.
  parent: z.number().int().nonnegative().nullable(),
  rect,
  opacity: unit,
})
const node = z.discriminatedUnion('kind', [
  base.extend({
    kind: z.literal('box'),
    fill: color.nullable(),
    gradient: z
      .object({
        angle: number,
        stops: z
          .array(z.object({ position: unit, color }))
          .min(2)
          .max(32),
      })
      .nullable(),
    shadows: z
      .array(
        z.object({
          color,
          x: number,
          y: number,
          blur: number.nonnegative().max(1000),
          spread: number,
          inset: z.boolean(),
        }),
      )
      .max(20),
    // Layer blur radius in CSS blur() pixels; older plugins ignore it.
    blur: number.nonnegative().max(1000).default(0),
    radius: z.tuple([number.nonnegative(), number.nonnegative(), number.nonnegative(), number.nonnegative()]),
    stroke: color.nullable(),
    strokeWidth: number.nonnegative().max(1000),
    clip: z.boolean(),
  }),
  base.extend({
    kind: z.literal('text'),
    runs: z.array(run).min(1).max(1000),
    align: z.enum(['LEFT', 'CENTER', 'RIGHT', 'JUSTIFIED']),
    lineHeight: number.positive().max(10000).nullable(),
  }),
  base.extend({ kind: z.literal('svg'), svg: z.string().max(2 * 1024 * 1024) }),
  base.extend({
    kind: z.literal('image'),
    asset: z.string().max(200),
    fit: z.enum(['FILL', 'FIT']),
  }),
])

export const figmaTransferSchema = z
  .object({
    format: z.literal('doop-figma'),
    version: z.literal(1),
    name: z.string().max(200),
    frames: z
      .array(
        z.object({
          id: z.string().max(200),
          name: z.string().max(200),
          width: number.positive().max(10000),
          height: number.positive().max(10000),
          nodes: z.array(node).max(MAX_FIGMA_EXPORT_NODES),
        }),
      )
      .min(1)
      .max(MAX_FIGMA_EXPORT_FRAMES),
    assets: z.record(z.string().max(12 * 1024 * 1024)),
    warnings: z.array(z.string().max(500)).max(200),
  })
  .superRefine((value, ctx) => {
    let count = 0
    for (const frame of value.frames) {
      count += frame.nodes.length
      frame.nodes.forEach((n, index) => {
        if (n.parent !== null && (n.parent >= index || frame.nodes[n.parent]?.kind !== 'box')) {
          ctx.addIssue({ code: 'custom', message: 'Invalid layer hierarchy' })
        }
        if (n.kind === 'image' && !Object.prototype.hasOwnProperty.call(value.assets, n.asset)) {
          ctx.addIssue({ code: 'custom', message: 'Missing image data' })
        }
      })
    }
    if (count > MAX_FIGMA_EXPORT_NODES)
      ctx.addIssue({ code: 'custom', message: 'Too many layers — export fewer frames' })
  })

export type FigmaTransfer = z.infer<typeof figmaTransferSchema>
export type FigmaExportNode = FigmaTransfer['frames'][number]['nodes'][number]
export type FigmaTextRun = Extract<FigmaExportNode, { kind: 'text' }>['runs'][number]
export type FigmaColor = z.infer<typeof color>

export function parseFigmaTransfer(text: string): FigmaTransfer {
  if (text.length > MAX_FIGMA_TRANSFER_BYTES) throw new Error('This transfer is too large. Export fewer frames.')
  let value: unknown
  try {
    value = JSON.parse(text)
  } catch {
    throw new Error('Copy frames from Doop, or choose a .doop file.')
  }
  const result = figmaTransferSchema.safeParse(value)
  if (!result.success)
    throw new Error('This transfer is invalid or from a newer Doop version. Export again and update the plugin.')
  return result.data
}
